import React from 'react';
import * as d3 from 'd3';
import { renderToString } from 'react-dom/server';
import { get as _get, isEqual, lowerCase } from 'lodash';

export default class ScriptDisplay extends React.Component {
  static defaultProps = {
    onElementMove: () => {},
    onLinkElementStart: () => {},
    onLinkElementComplete: () => {},
  };

  constructor(props) {
    super(props);
    this.IS_EDITABLE = props.is_editable;

    this.state = {
      data: [],
      valid: true,
      linkSource: [],
      zoomLevel: 1, // 1 = 100%, 0.5 = 50%, etc.
    };

    // BASIC DISP VARS

    this.DISPLAY_SETTINGS = {
      viewBox: {
        x: 2000,
        y: 10000,
        defaultPadding: {
          x: 350,
          y: 100,
        },
      },
      connectorLabel: {
        width: 80,
        height: 30,
        padding: {
          x: 8,
          y: 2,
        },
        maxTextLength: 10,
      },
      connectorLine: {
        maxArcRadius: 6,
        minWidth: 1.5,
        maxWidth: 40,
      },
      box: {
        minWidth: 240,
        height: 90,
        padding: {
          x: 10,
          y: 280,
        },
        icon: {
          width: 30,
          height: 30,
          dx: -10,
          dy: -15,
        },
      },
    };

    this.connectionMode = false;
    this.dragArray = [null, null];
  }

  componentDidMount() {
    this.buildSVGWrapper();
    this.appendDefs();
    if (this.props.data) {
      this.prepareToBuildScript();
      this.buildScript();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // ONLY rebuild from props if props.data actually changed
    if (!isEqual(prevProps.data, this.props.data)) {
      this.prepareToBuildScript();
      this.buildScript();
      return;
    }

    // If editingElement changed, do NOT rebuild data
    // Only visuals need updating
    if (!isEqual(prevProps.editingElement, this.props.editingElement)) {
      this.buildScript();
    }

    if (!isEqual(prevState.data, this.state.data)) {
      this.buildScript();
    }

    if (prevState.zoomLevel !== this.state.zoomLevel) {
      this.applyZoom();
    }
  }

  applyZoom = () => {
    if (this.svgGroup) {
      this.svgGroup.attr('transform', `scale(${this.state.zoomLevel})`);
    }
  };

  handleZoomChange = (e) => {
    const zoomLevel = parseFloat(e.target.value);
    this.setState({ zoomLevel });
  };

  prepareToBuildScript = () => {
    this.setState({ data: this.organizeScriptElements() });
    this.connectorWidthScale = this.getConnectorWidthScale();
  };

  getBoxWidth = (elem) => {
    let { DISPLAY_SETTINGS: disp } = this;
    let calc_length =
      disp.box.padding.x * 2 +
      (disp.connectorLabel.width + disp.connectorLabel.padding.x * 2) * elem.connectors.length;
    return calc_length < disp.box.minWidth ? disp.box.minWidth : calc_length;
  };

  getElemSelector = (element_hash) => {
    return 'g-' + element_hash;
  };

  displayOrTruncateTextWithEllipsis = (text, maxChars) => {
    if (!text || typeof text !== 'string') return;

    if (text.length > maxChars) {
      return text.slice(0, maxChars) + '...';
    } else {
      return text;
    }
  };

  getConnectorWidthScale = () => {
    this.maxResponseCount = Math.max(...this.state.data.map((d) => d.response_count));
    return d3
      .scaleLinear()
      .domain([0, this.maxResponseCount || 1])
      .range([
        this.DISPLAY_SETTINGS.connectorLine.minWidth,
        this.DISPLAY_SETTINGS.connectorLine.maxWidth,
      ]);
  };

  organizeScriptElements = () => {
    let data = [...this.props.data];

    let start = data.find((el) => el.elementtype === 'start');

    if (!start) return [];

    const min_left = Math.min(...data.map((d) => d.pos_left));
    const min_top = Math.min(...data.map((d) => d.pos_top));
    this.startBoxSavedOffset = {
      x: min_left,
      y: min_top,
    };

    start.level = 1;

    let validScript = true;

    let organized = [start];
    const addConnectors = (elem) => {
      if (!elem.level) elem.level = [...organized].pop().level + 1;
      elem.elementtype = lowerCase(elem.elementtype);
      elem.connectors.forEach((con) => {
        let existingIndex = organized.findIndex((el) => el.element_hash === con.next_el_hash);
        if (existingIndex > -1) {
          if (organized[existingIndex].level <= elem.level) {
            organized[existingIndex].level = elem.level + 1;
          }
        } else {
          if (!con.next_el_hash) {
            return;
          }

          let connectedElem = data.find((el) => el.element_hash === con.next_el_hash);

          if (!connectedElem) {
            console.warn('Script Invalid: dangling connector', con);
            return;
          }

          if (!connectedElem.level || connectedElem.level <= elem.level) {
            connectedElem.level = elem.level + 1;
          }

          if (!organized.find((e) => e.element_hash === connectedElem.element_hash)) {
            organized.push(connectedElem);
          }
        }

        con.key = this.getConnectorKey({ ...con, parentData: elem });
      });
      if (!elem.connectors.length)
        elem.connectors.push({
          ordinal_position: 1,
          key: this.getConnectorKey({ parentData: elem }),
        });
    };

    if (!validScript) return [];
    data.forEach((d) => addConnectors({ ...d }));
    return data;
  };

  getElementTransformation = (domElem) => {
    try {
      var matrix = domElem.transform.baseVal.consolidate().matrix;
      return [matrix.e, matrix.f];
    } catch {
      return [0, 0];
    }
  };

  getConnectorLine = (conn, idx, connArr) => {
    let { DISPLAY_SETTINGS: disp } = this;

    // Fix: Ensure connArr is an array and filter out any null/undefined entries
    const safeConnArr = connArr ? Array.from(connArr).filter((d) => d && d.__data__) : [];

    const parent = document.getElementById(this.getElemSelector(conn.parentData.element_hash));
    const destination = document.getElementById(this.getElemSelector(conn.next_el_hash));

    if (!parent || !destination) return null;

    let sourceOffset = this.getElementTransformation(parent);
    let destinationOffset = this.getElementTransformation(destination);

    const destinationWidth = +(destination.getAttribute('box-width') || disp.box.minWidth);

    const goingRight = sourceOffset[0] < destinationOffset[0];

    let connOffset;
    if (goingRight) {
      // Use safeConnArr here
      connOffset = safeConnArr.reduce(
        (r, d, i) => (i < idx ? r - (d.__data__.stroke_width - 2) : r),
        safeConnArr.reduce((r, d) => r + (d.__data__?.stroke_width || 0), 0)
      );
    } else {
      // Use safeConnArr here
      connOffset = safeConnArr.reduce(
        (r, d, i) => (i < idx ? r + (d.__data__.stroke_width - 2) : r),
        0
      );
    }

    destinationOffset[0] = destinationOffset[0] + destinationWidth / 2;

    sourceOffset[0] = sourceOffset[0] + conn.dx;
    sourceOffset[1] =
      sourceOffset[1] + conn.dy + disp.connectorLabel.height + disp.connectorLabel.padding.y;

    const start = {
      x: disp.connectorLabel.width / 2,
      y: disp.connectorLabel.height + disp.connectorLabel.padding.y,
    };

    const end = {
      x: destinationOffset[0] - sourceOffset[0] + (goingRight ? -connOffset : connOffset),
      y:
        destinationOffset[1] -
        sourceOffset[1] +
        (disp.connectorLabel.height + disp.connectorLabel.padding.y),
    };

    if (end.y >= start.y) {
      const controlPoint1 = {
        x: start.x,
        y: start.y + (end.y - start.y) / 2 + connOffset,
      };

      const controlPoint2 = {
        x: end.x,
        y: controlPoint1.y,
      };

      controlPoint1.arcRadius =
        Math.abs(controlPoint1.x - controlPoint2.x) < disp.connectorLine.maxArcRadius
          ? Math.abs(controlPoint1.x - controlPoint2.x)
          : disp.connectorLine.maxArcRadius;

      controlPoint2.arcRadius =
        Math.abs(controlPoint2.y - end.y) < disp.connectorLine.maxArcRadius
          ? Math.abs(controlPoint2.y - end.y)
          : disp.connectorLine.maxArcRadius;

      const lineGen = d3.path();
      lineGen.moveTo(start.x, start.y);
      lineGen.lineTo(controlPoint1.x, controlPoint1.y - disp.connectorLine.maxArcRadius);
      lineGen.arcTo(
        controlPoint1.x,
        controlPoint1.y,
        controlPoint2.x,
        controlPoint2.y,
        controlPoint1.arcRadius
      );
      lineGen.arcTo(controlPoint2.x, controlPoint2.y, end.x, end.y, controlPoint2.arcRadius);
      lineGen.lineTo(end.x, end.y);
      return lineGen;
    }

    const verticalSpine = 40;
    const clearanceAboveDestination = 40;
    const maxR = disp.connectorLine.maxArcRadius;

    const spineY = start.y + verticalSpine;
    const topY = end.y - clearanceAboveDestination;

    const midX = start.x + (end.x - start.x) / 2;

    const lineGen = d3.path();
    lineGen.moveTo(start.x, start.y);

    lineGen.lineTo(start.x, spineY - maxR);

    const horizontalDir = end.x > start.x ? 1 : -1;
    lineGen.arcTo(start.x, spineY, start.x + horizontalDir * maxR, spineY, maxR);

    lineGen.lineTo(midX - horizontalDir * maxR, spineY);

    lineGen.arcTo(midX, spineY, midX, spineY - maxR, maxR);

    lineGen.lineTo(midX, topY + maxR);

    lineGen.arcTo(midX, topY, midX + horizontalDir * maxR, topY, maxR);

    lineGen.lineTo(end.x - horizontalDir * maxR, topY);

    lineGen.arcTo(end.x, topY, end.x, topY + maxR, maxR);

    lineGen.lineTo(end.x, end.y);

    return lineGen;
  };

  getTranslationOfGroup = (d) => {
    let { DISPLAY_SETTINGS: disp } = this;

    return {
      x: d.pos_left + disp.viewBox.defaultPadding.x,
      y: d.pos_top + disp.viewBox.defaultPadding.y,
    };
  };

  selectElementGroupData = (elem) => {
    return { ...elem, width: this.getBoxWidth(elem) };
  };

  getTitleHTML = (elem) => {
    return `<p>${elem.display_name}</p>`;
  };

  buildScriptBox = (elem) => {
    return (
      <div
        className={`script-box ${
          elem.element_hash === _get(this.props, 'editingElement.element_hash', 'xx')
            ? 'editing'
            : ''
        }`}
        style={{ height: '100%', width: '100%' }}
      >
        <h6 className={'primary-text script-box__title'}>
          {this.displayOrTruncateTextWithEllipsis(elem.display_name, 60)}
        </h6>
        <p className={'script-box__prompt'}>
          {this.displayOrTruncateTextWithEllipsis(elem.prompt, 100)}
        </p>
      </div>
    );
  };

  buildScriptElementIcon = (elem) => {
    return (
      <div className={'script-box-icon is-flex'}>
        <i className={elem.iconClass} />
      </div>
    );
  };

  renderConnLabel = (con) => {
    if (this.props.is_editable) {
      return (
        <div className={`script-con `}>
          <span className={'script-con__name'}>
            {this.displayOrTruncateTextWithEllipsis(
              con.value_text,
              this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
            )}
          </span>
          <span
            className={`script-con__connect_button ${
              this.state.linkSource.map((d) => d.key).includes(con.key)
                ? 'selected-conn-source'
                : ''
            }`}
            id={'connect-button-' + con.key}
          />
        </div>
      );
    } else {
      return (
        <div className={'script-con'}>
          <span className={'script-con__name'}>
            {this.displayOrTruncateTextWithEllipsis(
              con.value_text,
              this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
            )}
          </span>
          <span className={'script-con__count'}>{con.response_count}</span>
        </div>
      );
    }
  };

  setConnectorValueText = (elem, conn) => {
    if (conn.value_text)
      return this.displayOrTruncateTextWithEllipsis(
        conn.value_text,
        this.DISPLAY_SETTINGS.connectorLabel.maxTextLength
      );

    if (this.props.is_editable) {
      return null;
    }

    if (['start'].includes(elem.elementtype)) {
      return 'Pass Through';
    } else {
      return 'Responses';
    }
  };

  selectConnectorData = (elem, idx, elemSelectionArray) => {
    let { DISPLAY_SETTINGS: disp } = this;

    let connectors = elem.connectors.map((c, i, a) => ({
      ...c,
      value_text: this.setConnectorValueText(elem, c),
      response_count: this.props.is_editable
        ? c.response_count || c.response_count === 0
          ? c.response_count
          : elem.response_count
        : null,
      dx: this.props.is_editable
        ? disp.connectorLabel.width * i +
          (this.getBoxWidth(elem) - disp.connectorLabel.width * a.length) / 2
        : 4,
      dy: disp.box.height + 10,
      parentData: elem,
    }));

    if (!['start', 'question', 'end'].includes(elem.elementtype) && elem.connectors.length) {
      let prevLevel = elem.level - 1;
      let prevLevelElems = this.state.data.filter((e) => e.level === prevLevel);
      let connectingResponses = 0;
      let passThroughs = 0;

      prevLevelElems.forEach((e) => {
        e.connectors.forEach((c, i, a) => {
          if (c.next_el_hash === elem.element_hash) {
            connectingResponses += this.getConnResponseCountForInterpolation(
              { ...c, parentData: e },
              i,
              a
            );
          }
        });
      });

      const getNextQuestion = (next_el_hash, visited = new Set()) => {
        if (!next_el_hash || visited.has(next_el_hash)) {
          return null;
        }

        visited.add(next_el_hash);

        const nextElem = this.state.data.find((d) => d.element_hash === next_el_hash);

        if (!nextElem) return null;

        if (nextElem.elementtype === 'question') {
          return nextElem;
        }

        if (nextElem.elementtype === 'end' || !nextElem.connectors.length) {
          return null;
        }

        // Follow the first connector forward
        return getNextQuestion(nextElem.connectors[0].next_el_hash, visited);
      };

      let nextQuestion = getNextQuestion(connectors[0].next_el_hash);

      if (nextQuestion) {
        let nextQuestionRespCount = nextQuestion.response_count;
        let passThroughsPossible = nextQuestionRespCount - elem.response_count;
        if (passThroughsPossible > connectingResponses) {
          passThroughs = connectingResponses - elem.response_count;
        } else {
          passThroughs = passThroughsPossible - elem.response_count;
        }
      } else {
        passThroughs = connectingResponses - elem.response_count;
      }

      if (passThroughs > 0) {
        connectors.push({
          value_text: 'Pass Through',
          response_count: passThroughs,
          dx:
            disp.connectorLabel.width * connectors.length +
            (this.getBoxWidth(elem) - disp.connectorLabel.width * connectors.length) / 2,
          dy: disp.box.height + 10,
          parentData: elem,
          next_el_hash: connectors[0].next_el_hash,
        });
        if (elem.elementtype === 'speech') {
        }
      }
    }

    connectors = connectors
      .map((c, i, a) => ({
        ...c,
        response_count: this.getConnResponseCountForInterpolation(c, i, a),
      }))
      .map((c, i, a) => ({
        ...c,
        stroke_width: this.getConnStrokeWidth(c),
      }));
    return connectors;
  };

  getConnectorKey = (conn) => {
    return `${conn.value_text || 'single'}-${conn.parentData.element_hash}`;
  };

  getConnResponseCountForInterpolation = (d, i, a) => {
    let { response_count } = d;

    if (['start'].includes(d.parentData.elementtype)) {
      response_count = this.maxResponseCount;
    }

    return response_count;
  };

  getConnStrokeWidth = (conn) => {
    if (this.props.is_editable) return 1.5;
    return this.connectorWidthScale(conn.response_count);
  };

  getConnColor = (d, i, a) => {
    if (this.props.is_editable) {
      return d.color || '#a4a4a4';
    } else {
      if (d.next_el_display_name === 'END' || d.response_count === 0) return '#d4d4d4';
      if (d.value_text === 'Pass Through') return '#9b4dca';
      return '#1679c7';
    }
  };

  getConnOpacity = (d, i, a) => {
    if (d.next_el_display_name === 'END') {
      return '.4';
    } else {
      return '1';
    }
  };

  getConnBoxClickAction = () => {
    const _t = this;

    return function (event) {
      if (event?.stopPropagation) event.stopPropagation();

      const t = d3.select(this);
      const t_data = _get(t.data(), '[0]', null);
      if (!t_data) return;

      // 🔑 IMPORTANT: always start fresh (prevents “extra connector” updates)
      _t.clearBuildConnectionMode();

      // Disable all connectors globally; re-enable only on selected parent
      _t.svg.selectAll('.script-con').classed('disabled', true);

      const scriptElemGroupSelector =
        '#g-' + _get(t_data, 'parentData.element_hash', 'SELECT_FAILURE');

      if (t_data.parentData?.elementtype === 'multiselect') {
        const element_data = d3.select(scriptElemGroupSelector).data()[0];

        const newLinkSource = (element_data?.connectors || []).map((d) => ({
          ...d,
          parentData: element_data,
        }));

        _t.setState({ linkSource: newLinkSource }, () => {
          // enable that element’s connectors + highlight its buttons
          d3.select(scriptElemGroupSelector).selectAll('.script-con').classed('disabled', false);

          d3.select(scriptElemGroupSelector)
            .selectAll('.script-con__connect_button')
            .classed('selected-conn-source', true);
        });

        return;
      }

      // Normal case: single connector source
      _t.setState({ linkSource: [t_data] }, () => {
        d3.select(scriptElemGroupSelector).selectAll('.script-con').classed('disabled', false);

        _t.svg.selectAll('#connect-button-' + t_data.key).classed('selected-conn-source', true);
      });
    };
  };

  clearBuildConnectionMode = () => {
    this.setState({ linkSource: [] });
    this.svg.selectAll('.script-con').classed('disabled', false);
    this.svg.selectAll('.selected-conn-source').classed('selected-conn-source', false);
  };

  editConnectionStateData = (connectionKey, dataToInsert = {}) => {
    let { data: newData } = this.state;

    newData.forEach((d) => {
      d.connectors.forEach((conn) => {
        if (conn.key === connectionKey) {
          Object.keys(dataToInsert).forEach((k) => {
            conn[k] = dataToInsert[k];
          });
        }
      });
    });

    this.setState({ newData });
  };

  handleConnectionAssignment = (target_elem_hash) => {
    const target_element = this.props.data.find((d) => d.element_hash === target_elem_hash);

    if (!target_element || !this.state.linkSource.length) return;

    const source_element_hash = _get(this.state, 'linkSource[0].parentData.element_hash', '--');

    // Prevent connecting an element to itself (hard stop)
    if (target_elem_hash === source_element_hash) {
      console.warn('Connection cancelled: self-loop not allowed.');
      this.clearBuildConnectionMode();
      return;
    }

    // Prevent START from being a target
    if (target_elem_hash === 'start') {
      this.clearBuildConnectionMode();
      return;
    }

    let link_creates_loop = false;

    const checkForLoopInElement = (targ_elem) => {
      if (link_creates_loop) return;
      targ_elem.connectors.forEach((d) => {
        if (d.next_el_hash === source_element_hash) {
          link_creates_loop = true;
        } else {
          const next_target_element = this.props.data.find(
            (elem) => elem.element_hash === d.next_el_hash
          );
          if (next_target_element) checkForLoopInElement(next_target_element);
        }
      });
    };

    checkForLoopInElement(target_element);

    if (link_creates_loop) {
      console.warn('Connection cancelled: Loop detected.');
      return;
    }

    const updatedData = structuredClone
      ? structuredClone(this.props.data)
      : JSON.parse(JSON.stringify(this.props.data));

    updatedData.forEach((d) => {
      d.connectors.forEach((conn) => {
        if (this.state.linkSource.some((ls) => ls.key === conn.key)) {
          // HARD GUARD: never allow self-loop
          if (conn.parentData?.element_hash === target_elem_hash) {
            console.warn('Blocked self-connecting connector:', conn);
            return;
          }

          conn.next_el_hash = target_elem_hash;
        }
      });
    });

    if (this.props.onLinkElementComplete) {
      this.props.onLinkElementComplete(updatedData);
    }

    this.clearBuildConnectionMode();
  };

  handleXYUpdate = (element_hash, x, y) => {
    this.setState((prev) => ({
      data: prev.data.map((d) =>
        d.element_hash === element_hash ? { ...d, pos_left: x, pos_top: y } : d
      ),
    }));

    if (this.props.onElementMove) {
      this.props.onElementMove(element_hash, x, y);
    }
  };

  getDragHandler = () => {
    const _t = this;
    const disp = _t.DISPLAY_SETTINGS;

    return d3
      .drag()
      .on('drag', function (ev) {
        const t = d3.select(this);
        const elemdata = t.data()[0];

        const newX = ev.x - _t.getBoxWidth(elemdata) / 2 - disp.viewBox.defaultPadding.x;

        const newY = ev.y - disp.box.height / 2 - disp.viewBox.defaultPadding.y;

        if (!_t.dragArray[0]) {
          _t.dragArray = [newX, newY];
          return;
        }

        if (Math.abs(_t.dragArray[0] - newX) < 2 && Math.abs(_t.dragArray[1] - newY) < 2) {
          return;
        }

        const renderX = newX + disp.viewBox.defaultPadding.x;
        const renderY = newY + disp.viewBox.defaultPadding.y;

        t.attr('transform', `translate(${renderX}, ${renderY})`);

        _t.buildConnections();
      })
      .on('end', function (ev) {
        const t = d3.select(this);
        const elemdata = t.data()[0];

        const finalX = ev.x - _t.getBoxWidth(elemdata) / 2 - disp.viewBox.defaultPadding.x;

        const finalY = ev.y - disp.box.height / 2 - disp.viewBox.defaultPadding.y;

        _t.dragArray = [null, null];

        _t.handleXYUpdate(elemdata.element_hash, finalX, finalY);
      });
  };

  buildSVGWrapper = () => {
    let { DISPLAY_SETTINGS: disp } = this;

    this.svg = d3.select(this.d3Target).append('svg').classed('scriptResponses', true);

    // Create a group for all content that will be zoomed
    this.svgGroup = this.svg.append('g').attr('class', 'zoom-group');

    this.svg.on('dblclick', () => {
      if (this.state.linkSource.length) {
        this.clearBuildConnectionMode();
      }
    });
  };

  buildConnections = () => {
    let { DISPLAY_SETTINGS: disp } = this;
    let _t = this;

    let _enter = (selection) => {
      selection.attr('transform', (d) => `translate(${d.dx}, ${d.dy})`);

      // Create the label once
      selection
        .append('foreignObject')
        .attr('height', disp.connectorLabel.height)
        .attr('width', disp.connectorLabel.width)
        .classed('script-conn-label', true)
        .html((d) => renderToString(this.renderConnLabel(d)))
        .on('click', this.getConnBoxClickAction()); // Bind once here

      selection
        .append('path')
        .classed('connector-path', true) // Add a class to find it later
        .attr('d', (d, i, a) => this.getConnectorLine(d, i, a))
        .attr('stroke-width', (d) => d.stroke_width)
        .attr('stroke', this.getConnColor)
        .attr('opacity', this.getConnOpacity)
        .attr('fill', 'transparent');
    };

    let _update = (selection) => {
      // Get the most current data from state to ensure we aren't using stale closure data
      const currentData = this.state.data;

      selection.attr('transform', (d) => `translate(${d.dx}, ${d.dy})`);

      selection
        .selectAll('.connector-path')
        .attr('d', function (d, i, nodes) {
          // Find the fresh data for this specific connector
          const parentElem = currentData.find(
            (el) => el.element_hash === d.parentData.element_hash
          );
          const freshConnector = parentElem?.connectors.find((c) => c.key === d.key);

          // Merge the fresh next_el_hash into the local d object before drawing
          if (freshConnector) {
            d.next_el_hash = freshConnector.next_el_hash;
          }

          return _t.getConnectorLine(d, i, nodes);
        })
        .attr('stroke', this.getConnColor); // Ensure color updates (e.g., grey to blue)

      selection.each(function (d) {
        const isSelected = _t.state.linkSource.some((ls) => ls.key === d.key);
        d3.select(this)
          .selectAll('.script-con__connect_button')
          .classed('selected-conn-source', isSelected);
      });
    };

    let _exit = (selection) => {
      selection.remove();
    };

    this.conns = this.svgGroup
      .selectAll('g.script-group')
      .selectAll('g.connectors')
      .selectAll('g.connector')
      .data(
        (d, i, a) =>
          this.selectConnectorData(
            this.state.data.find((elem) => elem.element_hash === d.element_hash),
            i,
            this.state.data
          ),
        (conn) => conn.key
      )
      .join(
        (enter) => _enter(enter.append('g').classed('connector', true)),
        (update) => _update(update),
        (exit) => _exit(exit)
      );
  };

  buildScript = () => {
    let { DISPLAY_SETTINGS: disp } = this;
    let _t = this;
    let data = this.state.data;

    const _enter = (selection) => {
      let elems = selection
        .append('g')
        .classed('script-group', true)
        .attr('id', (d) => this.getElemSelector(d.element_hash))
        .attr('box-width', (d) => d.width)
        .attr('transform', (d) => {
          let translation = this.getTranslationOfGroup(d);
          return `translate(${translation.x}, ${translation.y})`;
        });

      elems.append('g').attr('class', 'connectors');

      let rects = elems.append('g').classed('script-rect-group', true);

      rects
        .append('rect')
        .classed('shadow-box', true)
        .attr('fill', 'transparent')
        .attr('stroke', 'black')
        .attr('stroke-width', 1)
        .attr('stroke-opacity', 0.4)
        .attr('height', disp.box.height)
        .attr('width', (d) => d.width)
        .style('filter', 'url(#drop-shadow)');

      let rectHTMLInnerElements = elems
        .append('foreignObject')
        .attr('height', disp.box.height)
        .attr('width', (d) => d.width)
        .classed('element-box-wrapper', true)
        .html((d) => renderToString(this.buildScriptBox(d)));

      let rectIcons = elems
        .append('foreignObject')
        .attr('height', disp.box.icon.height)
        .attr('width', disp.box.icon.width)
        .attr('transform', `translate(${disp.box.icon.dx}, ${disp.box.icon.dy})`)
        .html((d) => renderToString(this.buildScriptElementIcon(d)));

      this.getDragHandler()(elems);

      rectHTMLInnerElements.on('mouseover', function (d) {
        d3.select(this).selectAll('.script-box').classed('hovered', true);
      });

      rectHTMLInnerElements.on('mouseout', function (d) {
        d3.select(this).selectAll('.script-box').classed('hovered', false);
      });

      rectHTMLInnerElements.on('click', function (event, d) {
        // added event param
        event.stopPropagation(); // Prevents the click from being "stolen"
        if (_t.state.linkSource.length) {
          _t.handleConnectionAssignment(d.element_hash);
        }
      });

      rectHTMLInnerElements.on('dblclick', function () {
        if (_t.props.is_editable && _t.props.onEditElementRequest) {
          let t = d3.select(this);
          let element_data = _t.svgGroup
            .selectAll('g.script-group')
            .data()
            .find((ud) => ud.element_hash === t.data()[0].element_hash);
          if (element_data.elementtype === 'start') return;
          _t.props.onEditElementRequest(element_data);
        }
      });
    };

    const _update = (selection) => {
      let updatedData = selection.data();

      const getUpdatedElem = (element_hash) =>
        updatedData.find((ud) => ud.element_hash === element_hash) || {};

      selection
        .selectAll('.connectors')
        .data((d) => updatedData.find((ud) => ud.element_hash === d.element_hash));

      selection.attr('box-width', (d) => this.getBoxWidth(getUpdatedElem(d.element_hash)));

      selection
        .selectAll('.element-box-wrapper')
        .attr('width', (d) => this.getBoxWidth(getUpdatedElem(d.element_hash)))
        .html((d) => renderToString(this.buildScriptBox(getUpdatedElem(d.element_hash))));

      selection
        .selectAll('rect')
        .attr('width', (d) => this.getBoxWidth(getUpdatedElem(d.element_hash)));
    };

    const _exit = (selection) => {
      selection.remove();
    };

    this.svgGroup
      .selectAll('g.script-group')
      .data(data.map(this.selectElementGroupData), (d) => '' + d.element_hash)
      .join(
        (enter) => _enter(enter),
        (update) => _update(update),
        (exit) => _exit(exit)
      );

    this.buildConnections();
  };

  appendDefs = () => {
    const defs = this.svg.append('defs');

    const filter = defs.append('filter').attr('id', 'drop-shadow');

    filter.append('feDropShadow').attr('stdDeviation', 4).attr('flood-color', 'black');
  };

  render() {
    return (
      <>
        <div ref={(node) => (this.d3Target = node)} />

        {/* Zoom Control */}
        {this.props.is_editable && (
          <div
            style={{
              position: 'fixed',
              bottom: '20px',
              left: '20px',
              zIndex: 1000,
              background: 'white',
              padding: '12px 16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              minWidth: '220px',
            }}
          >
            <span
              style={{
                fontSize: '14px',
                fontWeight: 500,
                color: '#333',
                lineHeight: '1',
                whiteSpace: 'nowrap',
              }}
            >
              Zoom
            </span>

            {/* Slider wrapper fixes vertical alignment */}
            <div
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                height: '24px',
              }}
            >
              <input
                type="range"
                min="0.25"
                max="1"
                step="0.05"
                value={this.state.zoomLevel}
                onChange={this.handleZoomChange}
                style={{
                  width: '100%',
                  cursor: 'pointer',
                  margin: 0,
                  height: '16px',
                }}
              />
            </div>

            <span
              style={{
                fontSize: '13px',
                color: '#666',
                minWidth: '45px',
                textAlign: 'right',
                lineHeight: '1',
                whiteSpace: 'nowrap',
              }}
            >
              {Math.round(this.state.zoomLevel * 100)}%
            </span>
          </div>
        )}
      </>
    );
  }
}
